import type { DirectChatType } from '@constants/directChat';
import type { Pagination } from '@th-types/pageable.type';

// ─── Tab Enums (Worker Inbox) ──────────────────────────────────────────────

/** Worker-specific inbox tab display labels */
export enum WorkerInboxTabs {
  TODO = 'To Do',
  OTHER = 'Other',
  JOB_MESSAGES = 'Job Messages',
  DIRECT_MESSAGES = 'Direct Messages',
}

/** Worker-specific inbox tab URL keys */
export enum WorkerInboxTabKeys {
  TODO = 'todo',
  OTHER = 'other',
  JOB_MESSAGES = 'job-messages',
  DIRECT_MESSAGES = 'direct-messages',
}

// ─── Shared Interfaces ─────────────────────────────────────────────────────

export type { Pagination };

export interface UserState {
  id: number;
  dismissed: string | null;
  associatedType: string;
  associatedId: number;
  dateCreated: string;
  lastUpdated: string;
  new: boolean;
  type: string;
  userId: number;
}

export interface WorkInfo {
  workedDays: number;
  start: string;
  end: string;
  position: string;
  companyId?: number;
  companyName?: string;
}

export enum UserActionAssociatedTypeEnum {
  RATING_REQUEST = 'RATING_REQUEST',
  DIRECT_CHAT = 'DIRECT_CHAT',
  JOB_CHAT = 'JOB_CHAT',
}

export interface DismissItemsType {
  items: Array<{
    associatedType: UserActionAssociatedTypeEnum;
    associatedIds: number[];
  }>;
}

// ─── Direct Chats (shared) ─────────────────────────────────────────────────

export interface DirectChat {
  chatId: number;
  workerId: number | null;
  workerFullName: string;
  hasMultipleAuthors: boolean;
  unreadMessages: number;
  messageCount: number;
  lastMessageDateTime: string;
  lastMessageAuthorName: string;
  subject: string;
  firstUnreadMessageId?: number | null;
  companyId: number | null;
  companyName: string;
  companyLogoUrl: string;
  profileURLName?: string | null;
  type?: DirectChatType;
}

export interface DirectChatsResponse {
  directChats: DirectChat[];
  pagination: Pagination;
}

// ─── Job Chats (shared) ───────────────────────────────────────────────────

export interface JobChatAssignmentInfo {
  eventName: string;
  jobId: number;
  jobAssignmentId: number;
  start: string;
  end: string;
  position: string;
  address: Record<string, any>;
}

export interface JobChat {
  chatId: number;
  jobAssignmentId: number;
  jobAssignmentInfo: JobChatAssignmentInfo;
  workerId: number;
  workerFullName: string;
  memberId: number;
  memberFullName: string;
  companyName: string;
  companyLogoUrl: string;
  profileURLName?: string;
  unreadMessages: number;
  firstUnreadMessageId?: number | null;
  lastMessageDateTime: string;
}

export interface JobChatResponse {
  jobChats: JobChat[];
  pagination: Pagination;
}

// ─── To-Do Notifications ──────────────────────────────────────────────────

export enum TodoType {
  COMPANY_SHARED_JOB = 'COMPANY_SHARED_JOB',
  RECOMMENDED_JOB = 'RECOMMENDED_JOB',
  COMPANY_OFFERS_JOB = 'COMPANY_OFFERS_JOB',
  COMPANY_OFFERS_SHIFTS = 'COMPANY_OFFERS_SHIFTS',
  ENTER_HOURS = 'ENTER_HOURS',
  ENTER_RATING = 'ENTER_RATING',
  ENTER_RATING_PAYMENT = 'ENTER_RATING_PAYMENT',
}

export interface TodoItem {
  jobId: number | null;
  /** Usually null for RECOMMENDED_JOB; set on blast-share match (routes UI to legacy assignment page). */
  jobAssignmentId: number | null;
  jobRecommendationId: number | null;
  jobAssignmentStatus: string | null;
  shiftId: number | null;
  isTHJob: boolean;
  type: TodoType;
  todoMessage: string;
  companyLogoUrl: string;
  companyName?: string;
  profileURLName?: string;
  eventName?: string | null;
  createdAt?: string;
}

export interface TodoResponse {
  count: number;
  list: TodoItem[];
}

// ─── Worker Inbox Indicators (single-call badge counts) ────────────────────

export interface WorkerInboxIndicators {
  todoCount: number;
  notificationCount: number;
  messageCount: number;
  directMessagesCount: number;
  totalUnreadMessages: number;
  totalNotifications: number;
}

// ─── Chat Mode (shared between job messages & direct messages) ─────────────

export enum InboxChatModeEnum {
  INBOX = 'inbox',
  SENT = 'sent',
}

// ─── Query Parameters (shared) ─────────────────────────────────────────────

export enum InboxQueryParamsEnum {
  UNREAD = 'searchUnread',
  NEW = 'searchNew',
  SEARCH_TEXT = 'searchText',
  DATE_FROM = 'dateFrom',
  DATE_TO = 'dateTo',
  MAX = 'max',
  OFFSET = 'offset',
  MODE = 'mode',
  MARK_AS_READ = 'markAsRead',
}
